import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Link as RouterLink } from 'react-router-dom';

import { makeStyles, Card, CardContent, CardHeader, Box, Typography } from '@material-ui/core';
import MapMarkerPath from 'mdi-material-ui/MapMarkerPath';
import { ArrowBackIcon } from 'components/icons';
import { PropertyPicker } from 'components';
import { useTranslation } from 'react-i18next';
import TrayArrowUp from 'mdi-material-ui/UploadOutline';
import { useOrganizations, useProperties, useFields, useDevices } from 'hooks';
import { DashboardHelpers } from 'utils';
import FieldTiles from './FieldTiles';
import _ from 'lodash';
import { Divider } from '@mui/material';

const useStyles = makeStyles((theme) => ({
  backIcon: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(1),
  },
  card: {
    marginTop: theme.spacing(2),
    border: 'solid',
    borderColor: 'transparent',
    borderWidth: 2,
  },
  intro: {
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(1),
  },
  cardSelected: {
    marginTop: theme.spacing(2),
    border: 'solid',
    borderColor: theme.palette.link,
    borderWidth: 2,
  },
  cardHeader: {
    padding: theme.spacing(2),
    transition: 'all 0.2s ease-in-out',
    zIndex: 200,
  },
  cardContent: {
    paddingTop: 0,
  },
  cardCategory: {
    marginBottom: 18,
    color: '#999999',
  },
  button: {
    width: '100%',
    fontWeight: 300,
    fontSize: '20px',
    lineHeight: '24px',
  },
}));

const FieldSideBar = ({ action, setAction, updateField }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const { selectedOrganization, selectedOrganizationId } = useOrganizations();
  const { selectedPropertyId, properties, setSelectedProperty, userProperties, fetchUserProperties } = useProperties();
  const { fields, selectedField, setSelectedField } = useFields();
  const { getFieldDevices } = useDevices();

  const updateStep = (type, selectedField) => {
    if (type === 'draw') {
      updateField({ name: '', draw: true });
    } else if (type === 'upload') {
      updateField({ name: '', draw: false });
    } else if (type === 'edit') {
      updateField({ ...selectedField });
      setSelectedProperty(selectedField?.property_id);
    } else {
      updateField({ ...selectedField });
    }
  };

  const selectCard = (type) => (field) => {
    setAction(type);
    if (field) {
      setSelectedField(field?.id);
    }
    updateStep(type, field);
  };

  const filteredFields = DashboardHelpers.filteredFields(selectedPropertyId, selectedOrganizationId, fields);

  const groupTiles = () => {
    const groupedFieldTiles = [];
    const groupedByProperty = _.groupBy(filteredFields, (field) => field.property_id);

    if (UPfetched) {
      let userFields = [];

      _.forEach(groupedByProperty, (f) => {
        _.forEach(userProperties, (up) => {
          if (f.property_id == up.id) {
            userFields.push(f);
          }
        });
      });

      _.forEach(userFields, (fields, property_id) => {
        groupedFieldTiles.push(
          <Box key={property_id}>
            <Typography variant="h4" className={classes.intro}>
              {DashboardHelpers.getObjectById(property_id, undefined, properties)?.name}
            </Typography>
            <Divider />
            <FieldTiles
              getFieldDevices={getFieldDevices}
              fields={fields}
              selectedField={selectedField}
              setSelectedField={selectCard('edit')}
            />
          </Box>
        );
      });

      return groupedFieldTiles;
    }
  };

  const [UPfetched, setUPfetched] = useState(false);

  useEffect(() => {
    fetchUserProperties(selectedOrganizationId, '');
    setUPfetched(true);
  }, []);

  return selectedPropertyId ? (
    <Box display="flex" flexDirection="column" height="100%">
      <Box>
        <RouterLink to="/" className={classes.backIcon}>
          <ArrowBackIcon size="small" />
          <Typography style={{ paddingLeft: 8 }}>{t('home')}</Typography>
        </RouterLink>
        <Typography variant="h4" className={classes.intro}>
          {t('how_add_fields')}
        </Typography>
        <Box mt={1}>
          <PropertyPicker variant="subtitle2" />
        </Box>
        <Card
          className={action === 'draw' ? classes.cardSelected : classes.card}
          onClick={() => selectCard('draw')(undefined)}
        >
          <CardHeader
            className={classes.cardHeader}
            title={<Typography variant="h4">{t('draw_fields_title')}</Typography>}
            avatar={<MapMarkerPath />}
          />
          <CardContent className={classes.cardContent}>
            <Typography component="div" className={classes.cardCategory}>
              {t('draw_fields_subheader')}
            </Typography>
          </CardContent>
        </Card>
        <Card
          className={action === 'upload' ? classes.cardSelected : classes.card}
          onClick={() => selectCard('upload')(undefined)}
        >
          <CardHeader
            className={classes.cardHeader}
            title={<Typography variant="h4">{t('upload_fields_title')}</Typography>}
            avatar={<TrayArrowUp />}
          />
          <CardContent className={classes.cardContent}>
            <Typography component="div" className={classes.cardCategory}>
              {t('upload_fields_subheader')}
            </Typography>
          </CardContent>
        </Card>
      </Box>
      <Box>
        <Typography variant="h4" className={classes.intro}>
          {t('edit_field')}
        </Typography>
      </Box>
      <Box flexGrow={1} style={{ minHeight: 200, overflow: 'auto', marginBottom: 16 }}>
        <FieldTiles
          getFieldDevices={getFieldDevices}
          fields={filteredFields}
          selectedField={selectedField}
          setSelectedField={selectCard('edit')}
        />
      </Box>
    </Box>
  ) : (
    <Box>
      <RouterLink to="/" className={classes.backIcon}>
        <ArrowBackIcon size="small" />
        <Typography style={{ paddingLeft: 8 }}>{t('home')}</Typography>
      </RouterLink>
      {_.isEmpty(selectedOrganization) ? (
        <Typography variant="subtitle2">{t('select_organization_first')}</Typography>
      ) : (
        <Box>
          <Typography variant="h4" className={classes.intro}>
            {t('select_property_fields')}
          </Typography>
          <Box display="flex" flexDirection="column" height="100%">
            <Box mt={1}>
              <PropertyPicker variant="subtitle2" />
            </Box>
          </Box>
          <div style={{ minWidth: 200, maxHeight: '70vh', overflow: 'auto' }}>
            <Box flexGrow={1}>{groupTiles}</Box>
          </div>
        </Box>
      )}
    </Box>
  );
};

FieldSideBar.propTypes = {
  action: PropTypes.string,
  setAction: PropTypes.func.isRequired,
  updateField: PropTypes.func.isRequired,
};

FieldSideBar.defaultProps = {
  action: undefined,
};

export default FieldSideBar;
